import { waterTankThresholdColorScale } from '@weenat/client/dist/core/dss/irrigation'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { PlotSummary } from '@weenat/client/dist/resources/measurements.type'
import { WaterTankThresholdType } from '@weenat/client/dist/resources/thresholds.types'
import { useIntl } from '@weenat/wintl'
import Text from 'app/src/kit/Text'
import { ProfileBar } from 'app/src/kit/weedget/ReservoirProfile'
import { useUniverseAggregate } from 'app/state'
import { clamp, isNil, isNumber, max } from 'lodash-es'
import React, { useMemo } from 'react'
import { PlotListItemsVariants } from './PlotListItemMetricSummary'
import PlotListItemValue from './PlotListItemValue'
import PlotListItemValuesContainer from './PlotListItemValuesContainer'

const AGGREGATES = ['7D', 'DD', 'D', 'L'] as const

const getAggKey = (agg: (typeof AGGREGATES)[number]): string => {
  switch (agg) {
    case 'D':
      return 'd-0-measure'
    case 'DD':
      return 'd-1-measure'
    case '7D':
      return 'd-7-measure'
    case 'L':
      return 'real-time'
  }
}

const getWaterTankAggregates = (isVirtualWaterTank: boolean) => {
  const prefix = isVirtualWaterTank ? 'VIRTUAL_' : ''
  return {
    waterTankLevelMetric: `${prefix}WATER_TANK_LEVEL` as const,
    waterTankMaxMetric: `${prefix}WATER_TANK_MAX` as const,
    survivalWaterTankMetric: `${prefix}SURVIVAL_WATER_TANK` as const,
    readilyWaterTankMetric: `${prefix}READILY_WATER_CONTENT` as const
  }
}

interface CommonProps {
  /** Plot summary data */
  summaryData: PlotSummary['data']
  isRestricted: boolean
}

const TooltipVariant: React.FC<CommonProps> = ({ summaryData, isRestricted }) => {
  const [universeAggregate] = useUniverseAggregate()
  const summaryAgg = useMemo(() => {
    return universeAggregate === 'd-0-measure'
      ? 'D'
      : universeAggregate === 'd-1-measure'
        ? 'DD'
        : universeAggregate === 'd-7-measure'
          ? '7D'
          : 'L'
  }, [universeAggregate])

  const isVirtualWaterTank = Object.keys(summaryData).includes(
    `VIRTUAL_WATER_TANK_LEVEL_${summaryAgg}`
  )

  const {
    waterTankLevelMetric,
    waterTankMaxMetric,
    readilyWaterTankMetric,
    survivalWaterTankMetric
  } = getWaterTankAggregates(isVirtualWaterTank)
  const data = useMemo(() => {
    return {
      id: summaryAgg,
      value: {
        WATER_TANK_LEVEL: summaryData[`${waterTankLevelMetric}_${summaryAgg}`],
        WATER_TANK_MAX: summaryData[`${waterTankMaxMetric}_${summaryAgg}`],
        SURVIVAL_WATER_TANK: summaryData[`${survivalWaterTankMetric}_${summaryAgg}`],
        READILY_WATER_CONTENT: summaryData[`${readilyWaterTankMetric}_${summaryAgg}`]
      },
      key: universeAggregate,
      timestap: summaryData[`WATER_TANK_LEVEL_Li`]
    }
  }, [
    summaryData,
    universeAggregate,
    summaryAgg,
    waterTankLevelMetric,
    waterTankMaxMetric,
    survivalWaterTankMetric,
    readilyWaterTankMetric
  ])

  const { horizonThresholds, maxCapacity } = useMemo(() => {
    const horizonSurvivalWaterTank = data.value.SURVIVAL_WATER_TANK ?? 0
    const horizonReadilyWaterContent = data.value.READILY_WATER_CONTENT ?? 0
    const horizonWaterTankMax = data.value.WATER_TANK_MAX ?? 0

    const _maxCapacity = max(Object.values(data.value))
    const correctedMaxCapacity = !isNil(_maxCapacity) ? Math.round(1.05 * _maxCapacity) : null

    return {
      horizonThresholds: {
        survival_water_tank: horizonSurvivalWaterTank,
        rfu: horizonReadilyWaterContent,
        saturation: (correctedMaxCapacity ?? 0) - horizonWaterTankMax
      },

      maxCapacity: correctedMaxCapacity
    }
  }, [data.value])

  const { convertValue } = useConvertedValue()
  return (
    <PlotListItemValuesContainer $isRestricted={isRestricted}>
      <Flex $width={'100%'} $height={36}>
        <ProfileBar
          thresholds={horizonThresholds}
          maxCapacity={maxCapacity}
          value={
            isNumber(data?.value.WATER_TANK_LEVEL)
              ? clamp(0, data.value.WATER_TANK_LEVEL, 100)
              : undefined
          }
          isFirst={true}
          isLast={true}
          variant={'tooltip'}
        />
        <Flex $width={64} $justifyContent='flex-end' $alignItems='center' $px='md'>
          <Text $fontSize='sm' $fontWeight='medium' $textAlign='right'>
            {convertValue({
              metric: 'WATER_TANK_LEVEL',
              displayUnit: true,
              maximumFractionDigits: 0,
              value: isNumber(data?.value.WATER_TANK_LEVEL)
                ? clamp(0, data?.value.WATER_TANK_LEVEL, 100)
                : null,
              showEmptyState: true
            })}
          </Text>
        </Flex>
      </Flex>
    </PlotListItemValuesContainer>
  )
}

const PlotListVariant: React.FC<CommonProps> = ({ summaryData, isRestricted }) => {
  const { t } = useIntl()

  const isVirtualWaterTank = Object.keys(summaryData).some((key) =>
    AGGREGATES.map((agg) => `VIRTUAL_WATER_TANK_LEVEL_${agg}`).includes(key)
  )

  const {
    waterTankLevelMetric,
    waterTankMaxMetric,
    readilyWaterTankMetric,
    survivalWaterTankMetric
  } = getWaterTankAggregates(isVirtualWaterTank)

  const data = useMemo(() => {
    return AGGREGATES.map((agg) => {
      const WATER_TANK_LEVEL = summaryData[`${waterTankLevelMetric}_${agg}`]
      const WATER_TANK_MAX = summaryData[`${waterTankMaxMetric}_${agg}`]
      const SURVIVAL_WATER_TANK = summaryData[`${survivalWaterTankMetric}_${agg}`]
      const READILY_WATER_CONTENT = summaryData[`${readilyWaterTankMetric}_${agg}`]

      if (
        isNil(WATER_TANK_LEVEL) ||
        isNil(WATER_TANK_MAX) ||
        isNil(SURVIVAL_WATER_TANK) ||
        isNil(READILY_WATER_CONTENT)
      ) {
        return {
          id: agg,
          value: null,
          key: getAggKey(agg),
          thresholdColor: undefined
        }
      }
      const threshold: WaterTankThresholdType =
        WATER_TANK_LEVEL <= SURVIVAL_WATER_TANK
          ? 'survival_water_tank'
          : WATER_TANK_LEVEL >= WATER_TANK_MAX
            ? 'saturation'
            : 'rfu'

      return {
        id: agg,
        value: clamp(0, WATER_TANK_LEVEL, 100),
        key: getAggKey(agg),
        thresholdColor: waterTankThresholdColorScale(threshold)
      }
    })
  }, [
    summaryData,
    waterTankLevelMetric,
    waterTankMaxMetric,
    survivalWaterTankMetric,
    readilyWaterTankMetric
  ])

  return (
    <PlotListItemValuesContainer $isRestricted={isRestricted}>
      <Flex $height={24} $justifyContent='flex-end'>
        {data.map(({ value, key, thresholdColor }) => (
          <PlotListItemValue
            key={key}
            name={t(`map.universes.aggregate[${key}]`)}
            metric={'WATER_TANK_LEVEL'}
            variant={'list'}
            value={value}
            thresholdColor={thresholdColor}
          />
        ))}
      </Flex>
    </PlotListItemValuesContainer>
  )
}

interface WaterTankPlotListItemValuesProps extends CommonProps {
  variant: PlotListItemsVariants
}

const WaterTankPlotListItemValues: React.FC<WaterTankPlotListItemValuesProps> = ({
  summaryData,
  isRestricted,
  variant
}) => {
  return variant === 'tooltip' ? (
    <TooltipVariant summaryData={summaryData} isRestricted={isRestricted} />
  ) : (
    <PlotListVariant summaryData={summaryData} isRestricted={isRestricted} />
  )
}

export default WaterTankPlotListItemValues
